import React, { useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { updateDb } from "../services/updateDb";
import { useAppContext } from "../AppContext";
import MailingContentEditor from "./MailingContentEditor";
import MailingPreview from "./MailingPreview";
import useAutosave, { SaveStatus } from "./useAutosave";
import { Section, Segmented, StatusPill } from "./mailingUi";

const FILTERS = {
  expired: (env) => env.status === 'trial_expired' || env.status === 'subscription_expired',
  trial_expired: (env) => env.status === 'trial_expired',
  subscription_expired: (env) => env.status === 'subscription_expired',
  trial: (env) => env.status === 'trial',
  subscription: (env) => env.status === 'subscription',
  free: (env) => env.status === 'free',
  all: () => true,
};

const TYPES = [
  { key: 'reactivation', icon: 'icon-history', tone: 'orange' },
  { key: 'newsletter', icon: 'icon-mail', tone: 'blue' },
];

const formatDate = (value) => (value ? moment(value).format('ll') : '–');

const ComposeTab = ({ audience, defaults, drafts, onSent, active }) => {
  const { t } = useTranslation();
  const { showAlertMessage } = useAppContext();

  const [type, setType] = useState('reactivation');
  const [contents, setContents] = useState({
    reactivation: drafts?.reactivation || defaults.reactivation,
    newsletter: drafts?.newsletter || defaults.newsletter,
  });
  // Drafts are kept on the server, so edits survive leaving the page; only changed mailings are sent
  const autosave = useAutosave(contents, (value, saved) => ({
    campaigns: Object.fromEntries(Object.keys(value).filter((key) => JSON.stringify(value[key]) !== JSON.stringify(saved[key])).map((key) => [key, value[key]])),
  }));
  const [recipients, setRecipients] = useState('admins');
  const [newTrialDays, setNewTrialDays] = useState(14);
  const [filter, setFilter] = useState('expired');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [language, setLanguage] = useState('nl');
  const [showConfirm, setShowConfirm] = useState(false);
  const [sending, setSending] = useState(false);

  const content = contents[type];
  const trialDays = type === 'reactivation' ? Number(newTrialDays) || 0 : 0;

  const filterCounts = useMemo(() => Object.fromEntries(
    Object.entries(FILTERS).map(([key, match]) => [key, audience.filter(match).length]),
  ), [audience]);

  const visible = useMemo(() => audience
    .filter(FILTERS[filter])
    .filter((env) => !search || env.name.toLowerCase().includes(search.toLowerCase())), [audience, filter, search]);

  const selectedEnvironments = audience.filter((env) => selected.has(env.id));
  const recipientCount = selectedEnvironments.reduce((sum, env) => sum + (recipients === 'all' ? env.userCount : env.adminCount), 0);
  const trialGrantCount = trialDays > 0 ? selectedEnvironments.filter((env) => env.accessBlocked).length : 0;
  const allVisibleSelected = visible.length > 0 && visible.every((env) => selected.has(env.id));
  const someVisibleSelected = visible.some((env) => selected.has(env.id));

  const changeType = (newType) => {
    if (newType === type) return;
    setType(newType);
    setFilter(newType === 'reactivation' ? 'expired' : 'all');
    setSelected(new Set());
  };

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const toggleAllVisible = () => {
    const next = new Set(selected);
    visible.forEach((env) => (allVisibleSelected ? next.delete(env.id) : next.add(env.id)));
    setSelected(next);
  };

  const previewRequest = {
    type,
    content,
    newTrialDays: trialDays,
    environmentId: selectedEnvironments[0]?.id || null,
  };

  const send = async () => {
    setSending(true);
    const res = await updateDb('/api/mailings/send', {
      type,
      content,
      environmentIds: [...selected],
      recipients,
      newTrialDays: trialDays,
    });
    setSending(false);
    setShowConfirm(false);
    if (res.success) {
      showAlertMessage('success', t('mailing.campaign_started', { count: res.data.stats.recipients }));
      setSelected(new Set());
      onSent();
    } else {
      showAlertMessage('error', res.error || t('mailing.error'));
    }
  };

  return (
    <>
      <Modal show={showConfirm} onHide={() => setShowConfirm(false)}>
        <Modal.Header closeButton closeVariant="white">
          <Modal.Title>{t('mailing.confirm_send_title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t('mailing.confirm_send_text', { environments: selectedEnvironments.length, recipients: recipientCount })}</p>
          {trialGrantCount > 0 && <p><strong>{t('mailing.confirm_trial_text', { count: trialGrantCount, days: trialDays })}</strong></p>}
          <p className="mail-muted" style={{ fontSize: 13, marginBottom: 0 }}>{t('mailing.confirm_unsubscribed_note')}</p>
        </Modal.Body>
        <Modal.Footer>
          <div className="white-border-btn cancel-btn" onClick={() => setShowConfirm(false)}>{t('cancel')}</div>
          <div style={{ flexGrow: 1 }} />
          <button type="button" className="white-border-btn submit-btn" disabled={sending} onClick={send}>
            <i className="icon-mail" /> {sending ? t('mailing.sending') : t('mailing.send')}
          </button>
        </Modal.Footer>
      </Modal>

      <Section step={1} title={t('mailing.type')} subtitle={t('mailing.type_sub')}>
        <div className="mail-type-grid" role="radiogroup" aria-label={t('mailing.type')}>
          {TYPES.map((option) => (
            <button
              key={option.key}
              type="button"
              role="radio"
              aria-checked={type === option.key}
              className={`mail-type-card ${type === option.key ? 'active' : ''}`}
              onClick={() => changeType(option.key)}
            >
              <span className={`mail-stat-icon mail-tone-${option.tone}`}><i className={option.icon} /></span>
              <span>
                <span className="mail-type-title">{t(`mailing.type_${option.key}_title`)}</span>
                <span className="mail-type-text">{t(`mailing.type_${option.key}_help`)}</span>
              </span>
              <span className="mail-type-check"><i className="icon-check" /></span>
            </button>
          ))}
        </div>
      </Section>

      <Section step={2} title={t('mailing.audience')} subtitle={t('mailing.audience_sub')}>
        <div className="mail-chips" role="group" aria-label={t('mailing.filter')}>
          {Object.keys(FILTERS).map((key) => (
            <button key={key} type="button" className={`mail-chip ${filter === key ? 'active' : ''}`} aria-pressed={filter === key} onClick={() => setFilter(key)}>
              {t(`mailing.filter_${key}`)} <span className="mail-chip-count">{filterCounts[key]}</span>
            </button>
          ))}
        </div>

        <div className="mail-toolbar">
          <div className="mail-search">
            <i className="icon-search" />
            <input type="search" className="form-control" placeholder={t('mailing.search_environment')} value={search} onChange={(e) => setSearch(e.target.value)} aria-label={t('mailing.search_environment')} />
          </div>
          <div className="mail-toolbar-spacer" />
          <span className="mail-muted" style={{ fontSize: 13.5, fontWeight: 600 }}>{t('mailing.send_to_label')}</span>
          <Segmented
            ariaLabel={t('mailing.recipients')}
            value={recipients}
            onChange={setRecipients}
            options={[
              { value: 'admins', label: t('mailing.recipients_admins') },
              { value: 'all', label: t('mailing.recipients_all') },
            ]}
          />
        </div>

        <div className="mail-table-wrap scroll">
          <table className="mail-table">
            <thead>
              <tr>
                <td className="check">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    ref={(el) => { if (el) el.indeterminate = someVisibleSelected && !allVisibleSelected; }}
                    onChange={toggleAllVisible}
                    aria-label={t('mailing.select_all')}
                  />
                </td>
                <td>{t('environment')}</td>
                <td>{t('status')}</td>
                <td>{t('mailing.trial_expires')}</td>
                <td>{t('mailing.subscription_expires')}</td>
                <td className="num">{t('mailing.admins')}</td>
                <td className="num">{t('users')}</td>
                <td>{t('mailing.language')}</td>
              </tr>
            </thead>
            <tbody>
              {visible.map((env) => (
                <tr key={env.id} className={`clickable ${selected.has(env.id) ? 'selected' : ''}`} onClick={() => toggle(env.id)}>
                  <td className="check">
                    <input type="checkbox" checked={selected.has(env.id)} onChange={() => toggle(env.id)} onClick={(e) => e.stopPropagation()} aria-label={env.name} />
                  </td>
                  <td>
                    <strong>{env.name}</strong>
                    {env.trialExtensions > 0 && <span className="mail-tag" title={t('mailing.trial_extensions')}>+{env.trialExtensions}</span>}
                    <span className="mail-sub">{t('plan')}: {env.planId || '–'}</span>
                  </td>
                  <td><StatusPill status={env.status} /></td>
                  <td className="nowrap">{env.trialExpires > 0 ? formatDate(env.trialExpires) : '–'}</td>
                  <td className="nowrap">{formatDate(env.subscriptionExpires)}</td>
                  <td className="num">{env.adminCount}</td>
                  <td className="num">{env.userCount}</td>
                  <td>{env.language?.toUpperCase() || '–'}</td>
                </tr>
              ))}
              {visible.length === 0 && (
                <tr className="mail-empty-row"><td colSpan={8}>{t('mailing.no_environments')}</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {type === 'reactivation' && (
          <div className="mail-inline-field">
            <span className="mail-stat-icon mail-tone-green" style={{ width: 34, height: 34, fontSize: 15 }}><i className="icon-clock" /></span>
            <label htmlFor="newTrialDays">{t('mailing.new_trial_days_label')}</label>
            <input id="newTrialDays" type="number" min={0} max={90} className="form-control" value={newTrialDays} onChange={(e) => setNewTrialDays(e.target.value)} />
            <span>{t('mailing.days')}</span>
            <small className="mail-sub">{t('mailing.new_trial_days_help')}</small>
          </div>
        )}
      </Section>

      <Section
        step={3}
        title={t('mailing.content')}
        subtitle={t('mailing.content_sub')}
        actions={<SaveStatus status={autosave.status} error={autosave.error} />}
      >
        <div className="mail-compose-grid">
          <MailingContentEditor
            content={content}
            onChange={(value) => setContents({ ...contents, [type]: value })}
            showTrialBlock={type === 'reactivation'}
            language={language}
            onLanguageChange={setLanguage}
            onReset={() => setContents({ ...contents, [type]: defaults[type] })}
          />
          <MailingPreview request={previewRequest} language={language} active={active} />
        </div>
      </Section>

      <div className="mail-actionbar">
        <div className="mail-actionbar-text">
          {selected.size === 0 ? (
            <span className="mail-muted">{t('mailing.select_hint')}</span>
          ) : (
            <>
              <strong>{t('mailing.selected_environments', { count: selected.size })}</strong>
              <span className="mail-muted"> · {t('mailing.approx_recipients', { count: recipientCount })}</span>
              {trialGrantCount > 0 && <span className="mail-muted"> · {t('mailing.new_trials_summary', { count: trialGrantCount, days: trialDays })}</span>}
            </>
          )}
        </div>
        {selected.size > 0 && (
          <button type="button" className="mail-link-btn" onClick={() => setSelected(new Set())}>{t('mailing.clear_selection')}</button>
        )}
        <button type="button" className="rq-btn rq-btn-primary" disabled={selected.size === 0} onClick={() => setShowConfirm(true)}>
          <i className="icon-mail" /> {t('mailing.send_to', { count: selected.size })}
        </button>
      </div>
    </>
  );
};

export default ComposeTab;
